import { useCallback, useEffect, useMemo, useState } from 'react';
import { Auth } from 'aws-amplify/';
import { useAppDispatch, useAppSelector } from 'app/store';
import { selectAuth, setMfaVerified, setStepUpGrant } from 'app/store/authSlice';
import {
  deriveSessionSecurity,
  isBankLinkStepUpRequired,
  type IdTokenClaims,
  type SessionSecurity,
} from 'app/auth/sessionSecurity';

export interface UseSessionSecurityResult {
  /** Reactive view of the session's auth strength (for rendering/decisions). */
  security: SessionSecurity;
  /** True while the ID token is still being read. */
  loading: boolean;
  /**
   * Imperative, click-time check: does this action need an SMS step-up right
   * now? Recomputed against the current clock so a session that was MFA-fresh
   * at render but has since gone stale is correctly re-gated.
   */
  isBankLinkStepUpRequiredNow: () => boolean;
  /**
   * Record a successful step-up (call after a verified SMS OTP). Pass the signed
   * grant from verifyBankLinkStepUp so the next Plaid link-token request can
   * present it to the server-side gate (SEE-221).
   */
  recordStepUpVerified: (grant?: string) => void;
}

/**
 * Foundation hook for SEE-212 / SEE-221: exposes the current session's MFA
 * level and the bank-link step-up decision. Built to extend to new login
 * methods (passkey, etc.) without callers changing — they only ask "is a
 * step-up required?".
 */
export function useSessionSecurity(): UseSessionSecurityResult {
  const dispatch = useAppDispatch();
  const { mfaVerifiedAt } = useAppSelector(selectAuth);
  const [claims, setClaims] = useState<IdTokenClaims | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    Auth.currentSession()
      .then((session) => {
        if (active) setClaims(session.getIdToken().payload as IdTokenClaims);
      })
      .catch(() => {
        if (active) setClaims(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const security = useMemo(
    () => deriveSessionSecurity({ claims, stepUpVerifiedAt: mfaVerifiedAt }),
    [claims, mfaVerifiedAt]
  );

  const isBankLinkStepUpRequiredNow = useCallback(() => {
    // Recompute with a fresh clock so the freshness window is evaluated at the
    // moment of the click, not at last render.
    const current = deriveSessionSecurity({ claims, stepUpVerifiedAt: mfaVerifiedAt, now: Date.now() });
    return isBankLinkStepUpRequired({ security: current });
  }, [claims, mfaVerifiedAt]);

  const recordStepUpVerified = useCallback(
    (grant?: string) => {
      dispatch(setMfaVerified(Date.now()));
      if (grant) dispatch(setStepUpGrant(grant));
    },
    [dispatch]
  );

  return { security, loading, isBankLinkStepUpRequiredNow, recordStepUpVerified };
}

export default useSessionSecurity;
